import { api, bearer } from './client';
import type { components } from './generated/entitlements';

export type OnboardingProfile = components['schemas']['OnboardingProfileResponse'];
export type ProfileResult =
  | { kind: 'ok'; profile: OnboardingProfile }
  | { kind: 'rate_limited' }
  | { kind: 'failed'; statusClass: '4xx' | '5xx' | 'network' };
export type UpdateNameResult = 'ok' | 'invalid_name' | 'failed';

export const PROFILE_TIMEOUT_MS = 4_000;

function statusClassOf(status: number | undefined): '4xx' | '5xx' | 'network' {
  if (status === undefined) return 'network';
  if (status >= 500) return '5xx';
  return status >= 400 ? '4xx' : 'network';
}

export async function fetchOnboardingProfile(token: string, signal?: AbortSignal): Promise<ProfileResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROFILE_TIMEOUT_MS);
  const forward = () => controller.abort();
  if (signal?.aborted) controller.abort();
  else signal?.addEventListener('abort', forward, { once: true });
  try {
    const { data, error, response } = await api.POST('/me/onboarding/profile', {
      headers: bearer(token),
      signal: controller.signal,
    });
    if (response?.status === 429) return { kind: 'rate_limited' };
    if (error || !data) return { kind: 'failed', statusClass: statusClassOf(response?.status) };
    return { kind: 'ok', profile: data };
  } catch {
    return { kind: 'failed', statusClass: 'network' };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', forward);
  }
}

export async function updateName(
  token: string,
  body: components['schemas']['UpdateNameRequest'],
): Promise<UpdateNameResult> {
  try {
    const { error, response } = await api.PUT('/me/name', { body, headers: bearer(token) });
    // A non-2xx with an empty body leaves `error` undefined, so success is the status, not its absence.
    if (!error && response.ok) return 'ok';
    const code = (error as { error?: string } | undefined)?.error;
    return response.status === 400 && code === 'invalid_name' ? 'invalid_name' : 'failed';
  } catch {
    return 'failed';
  }
}
